<template>
  <div class="reset-password-container">
    <div class="reset-password-card">
      <h2 class="reset-password-title">
        {{ isResetMode ? 'Reset Your Password' : 'Forgot Password' }}
      </h2>
      <p class="reset-password-subtitle">
        {{ isResetMode 
          ? 'Enter your new password below' 
          : 'Enter your email to receive password reset instructions' 
        }}
      </p>
      
      <form @submit.prevent="submitForm" class="reset-password-form">
        <div v-if="error" class="error-message">
          {{ error }}
        </div>
        <div v-if="success" class="success-message">
          {{ success }}
        </div>
        
        <!-- Request Reset Form -->
        <template v-if="!isResetMode">
          <div class="form-group">
            <label for="email">Email address</label>
            <input 
              type="email" 
              v-model="email" 
              id="email" 
              placeholder="Enter your email"
              required 
              class="form-input"
            />
          </div>
        </template>

        <!-- Reset Password Form -->
        <template v-else>
          <div class="form-group">
            <label for="newPassword">New Password</label>
            <input 
              type="password" 
              v-model="newPassword" 
              id="newPassword" 
              placeholder="Enter your new password"
              required 
              class="form-input"
            />
          </div>

          <div class="form-group">
            <label for="confirmPassword">Confirm Password</label>
            <input 
              type="password" 
              v-model="confirmPassword" 
              id="confirmPassword" 
              placeholder="Confirm your new password"
              required 
              class="form-input"
            />
          </div>
        </template>

        <button type="submit" class="reset-button" :disabled="isLoading">
          {{ isLoading 
            ? (isResetMode ? 'Resetting...' : 'Sending...') 
            : (isResetMode ? 'Reset Password' : 'Send Reset Instructions')
          }}
        </button>

        <p class="login-link">
          {{ isResetMode ? 'Remember your password?' : 'Remembered your password?' }}
          <router-link to="/login">Sign in</router-link>
        </p>
      </form>
      <VersionDisplay class="version-display" />
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import VersionDisplay from '@/components/VersionDisplay.vue';

export default {
  components: {
    VersionDisplay
  },
  data() {
    return {
      email: '',
      token: '',
      newPassword: '',
      confirmPassword: '',
      success: '',
      error: '',
      isLoading: false,
      isResetMode: false
    };
  },
  created() {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    if (token) {
      this.token = token;
      this.isResetMode = true;
    }
  },
  methods: {
    async requestReset() {
      if (!this.email) {
        this.error = 'Please enter your email address';
        return;
      }

      this.isLoading = true;
      this.error = '';
      this.success = '';

      try {
        const response = await axios.post('/auth/request-reset', {
          email: this.email
        });
        
        this.success = response.data.message;
        if (process.env.NODE_ENV === 'development' && response.data.resetToken) {
          const resetUrl = `${window.location.origin}/reset-password?token=${response.data.resetToken}`;
          this.success += `\nReset URL (development only): ${resetUrl}`;
        }
      } catch (error) {
        console.error('Reset request error:', error);
        this.error = error.response?.data?.message || 'Failed to process reset request';
      } finally {
        this.isLoading = false;
      }
    },

    async resetPassword() {
      if (!this.newPassword || !this.confirmPassword) {
        this.error = 'Please enter and confirm your new password';
        return;
      }

      if (this.newPassword !== this.confirmPassword) {
        this.error = 'Passwords do not match';
        return;
      }

      this.isLoading = true;
      this.error = '';
      this.success = '';

      try {
        const response = await axios.post('/auth/reset-password', {
          token: this.token,
          newPassword: this.newPassword
        });
        
        this.success = response.data.message;
        setTimeout(() => {
          this.$router.push('/login');
        }, 3000);
      } catch (error) {
        console.error('Password reset error:', error);
        this.error = error.response?.data?.message || 'Failed to reset password';
      } finally {
        this.isLoading = false;
      }
    },

    submitForm() {
      if (this.isResetMode) {
        this.resetPassword();
      } else {
        this.requestReset();
      }
    }
  }
};
</script>

<style scoped>
.reset-password-container {
  min-height: calc(100vh - 64px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background-color: var(--background-alt);
}

.reset-password-card {
  background-color: var(--background);
  padding: 2.5rem;
  border-radius: 1rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  width: 100%;
  max-width: 400px;
}

.reset-password-title {
  font-size: 1.875rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
}

.reset-password-subtitle {
  color: var(--text-secondary);
  margin-bottom: 2rem;
}

.reset-password-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
}

.form-input {
  padding: 0.75rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  font-size: 1rem;
  width: 100%;
  transition: border-color 0.2s ease;
}

.form-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
}

.reset-button {
  background-color: var(--primary-color);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
  width: 100%;
}

.reset-button:hover {
  background-color: var(--primary-hover);
}

.reset-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.error-message {
  color: #dc2626;
  background-color: #fee2e2;
  padding: 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
}

.success-message {
  color: #059669;
  background-color: #d1fae5;
  padding: 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  white-space: pre-line;
}

.login-link {
  text-align: center;
  color: var(--text-secondary);
  font-size: 0.875rem;
}

.login-link a {
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 500;
}

.login-link a:hover {
  text-decoration: underline;
}

.version-display {
  margin-top: 2rem;
  text-align: center;
  color: var(--text-secondary);
  font-size: 0.75rem;
}

@media (max-width: 640px) {
  .reset-password-card {
    padding: 1.5rem;
  }
}
</style>
